import { useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { useSnackbar } from '../Context/SnackbarContext';
import {
  fetchScheduleSettings as apiFetchScheduleSettings,
  createScheduleSetting as apiCreateScheduleSetting,
  updateScheduleSetting as apiUpdateScheduleSetting,
  deleteScheduleSetting as apiDeleteScheduleSetting,
  optOutScheduleSetting as apiOptOutScheduleSetting,
  optInScheduleSetting as apiOptInScheduleSetting,
  resumeScheduleSetting as apiResumeScheduleSetting,
  runScheduleSetting as apiRunScheduleSetting,
  ScheduleSettingPayload,
  ScheduleViewPayload
} from '../Api/ScheduleSettings/scheduleSettingsApi';
import { refusalMessage } from '../Api/ScheduleSettings/scheduleRefusalMessage';
import { addError } from './Error';

export type { ScheduleSettingPayload, ScheduleViewPayload };

/** One tab of a report: the saved view it shows and the name it carries in the viewer and the workbook. */
export interface ScheduleView {
  id: string;
  view_key: string;
  tab_name: string;
  position: number;
}

export interface ScheduleSetting {
  id: string;
  name: string;
  schedule_rule: Record<string, unknown>;
  start_date: string;
  end_date?: string | null;
  recipients: string[];
  recipient_portal_user_ids: number[];
  send_email: boolean;
  share_with_team: boolean;
  include_executive_summary: boolean;
  last_run_on?: string | null;
  paused_at?: string | null;
  paused_reason?: string | null;
  view_key: string;
  views?: ScheduleView[];
  // Present on an API that moves a report along with a renamed view; one without it would delete the report.
  follows_view_rename?: boolean;
  project_id?: string | null;
  portal_user_id: number;
  account_ids: string[];
  created_at: string;
  updated_at: string;
}

export const viewsByPosition = (views: ScheduleView[] = []): ScheduleView[] =>
  [...views].sort((first, second) => first.position - second.position);

const TABS_NOT_KEPT_MESSAGE =
  'The server did not save the views of this report as sent. Reload the page, and try again once the update has finished.';

// A server that predates tabs ignores `views` and answers without them; one that kept some, or renamed or reordered
// them, answers with a different list.
const keptEveryTab = (payload: ScheduleSettingPayload, saved: ScheduleSetting): boolean => {
  if (!payload.views) return true;

  const savedViews = viewsByPosition(saved.views);
  return (
    savedViews.length === payload.views.length &&
    payload.views.every(
      (view, index) => savedViews[index].view_key === view.view_key && savedViews[index].tab_name === view.tab_name
    )
  );
};

let latestRead = 0;
let latestSettled: Promise<boolean> = Promise.resolve(true);

const schedulesSubject = new BehaviorSubject<ScheduleSetting[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);

const emitErrorState = (message?: string) => addError('schedule-settings', message);

export const getSchedulesSubject = (): BehaviorSubject<ScheduleSetting[]> => schedulesSubject;
export const getLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;
export const getSchedules = (): ScheduleSetting[] => schedulesSubject.value;

export interface SaveScheduleOptions {
  // Called with the schedule as soon as the API stored it, even when the save is then reported as not kept.
  onStored?: (schedule: ScheduleSetting) => void;
}

export interface UseScheduleSettingsReturn {
  schedules: ScheduleSetting[];
  loading: boolean;
  // False when the list could not be read, so a caller can tell an empty list from a missing one. A read that a
  // later one overtook answers what the latest read answers.
  fetchSchedules: () => Promise<boolean>;
  saveSchedule: (
    payload: ScheduleSettingPayload,
    existingId?: string,
    options?: SaveScheduleOptions
  ) => Promise<ScheduleSetting | null>;
  deleteSchedule: (id: string) => Promise<boolean>;
  optOut: (id: string) => Promise<ScheduleSetting | null>;
  optIn: (id: string) => Promise<ScheduleSetting | null>;
  resume: (id: string) => Promise<ScheduleSetting | null>;
  runSchedule: (id: string) => Promise<boolean>;
}

export const useScheduleSettings = (): UseScheduleSettingsReturn => {
  const [schedules, setSchedules] = useState<ScheduleSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const { setSnack } = useSnackbar();

  const fetchSchedules = async (): Promise<boolean> => {
    setLoading(true);
    loadingSubject.next(true);
    latestRead += 1;
    const read = latestRead;
    const settled = (async (): Promise<boolean> => {
      const { data, error } = await apiFetchScheduleSettings();
      if (read !== latestRead) return latestSettled;
      if (data) {
        schedulesSubject.next(data);
        emitErrorState();
      }
      if (error) emitErrorState(error.message);
      loadingSubject.next(false);
      return !!data;
    })();
    latestSettled = settled;
    const ok = await settled;
    if (ok) setSchedules(schedulesSubject.value);
    setLoading(false);
    return ok;
  };

  const saveSchedule = async (
    payload: ScheduleSettingPayload,
    existingId?: string,
    options: SaveScheduleOptions = {}
  ): Promise<ScheduleSetting | null> => {
    const { data, error } = existingId
      ? await apiUpdateScheduleSetting(existingId, payload)
      : await apiCreateScheduleSetting(payload);
    if (data) {
      const updated = existingId
        ? schedulesSubject.value.map((s) => (s.id === existingId ? data : s))
        : [...schedulesSubject.value, data];
      schedulesSubject.next(updated);
      setSchedules(updated);
      options.onStored?.(data);
      if (!keptEveryTab(payload, data)) {
        setSnack({ message: TABS_NOT_KEPT_MESSAGE, type: 'error', open: true });
        return null;
      }
      setSnack({ message: 'Schedule saved successfully', type: 'success', open: true });
    }
    if (error) setSnack({ message: refusalMessage(error, 'Error saving schedule'), type: 'error', open: true });
    return data;
  };

  const deleteSchedule = async (id: string): Promise<boolean> => {
    const { error } = await apiDeleteScheduleSetting(id);
    if (!error) {
      const updated = schedulesSubject.value.filter((s) => s.id !== id);
      schedulesSubject.next(updated);
      setSchedules(updated);
      setSnack({ message: 'Schedule deleted', type: 'success', open: true });
      return true;
    }
    setSnack({ message: `Error deleting schedule: ${error.message}`, type: 'error', open: true });
    return false;
  };

  const optOut = async (id: string): Promise<ScheduleSetting | null> => {
    const { data, error } = await apiOptOutScheduleSetting(id);
    if (data) {
      const updated = schedulesSubject.value.map((s) => (s.id === id ? data : s));
      schedulesSubject.next(updated);
      setSchedules(updated);
      return data;
    }
    if (error) setSnack({ message: `Error opting out: ${error.message}`, type: 'error', open: true });
    return null;
  };

  const optIn = async (id: string): Promise<ScheduleSetting | null> => {
    const { data, error } = await apiOptInScheduleSetting(id);
    if (data) {
      const updated = schedulesSubject.value.map((s) => (s.id === id ? data : s));
      schedulesSubject.next(updated);
      setSchedules(updated);
      return data;
    }
    if (error) setSnack({ message: `Error opting in: ${error.message}`, type: 'error', open: true });
    return null;
  };

  const resume = async (id: string): Promise<ScheduleSetting | null> => {
    const { data, error } = await apiResumeScheduleSetting(id);
    if (data) {
      const updated = schedulesSubject.value.map((s) => (s.id === id ? data : s));
      schedulesSubject.next(updated);
      setSchedules(updated);
      setSnack({ message: 'Schedule resumed', type: 'success', open: true });
      return data;
    }
    if (error) {
      setSnack({ message: refusalMessage(error, 'Error resuming schedule'), type: 'error', open: true });
    }
    return null;
  };

  const runSchedule = async (id: string): Promise<boolean> => {
    const { error } = await apiRunScheduleSetting(id);
    if (error) {
      setSnack({ message: refusalMessage(error, 'Error sending schedule'), type: 'error', open: true });
    }
    return !error;
  };

  return { schedules, loading, fetchSchedules, saveSchedule, deleteSchedule, optOut, optIn, resume, runSchedule };
};
